/**
 * Client HTTP pour l'API CAREX.
 *
 * Wrapper léger autour de fetch() avec :
 * - Base URL automatique (/api/v1)
 * - JSON par défaut
 * - Cookies httpOnly envoyés automatiquement
 * - Retry sur 401 via refresh token
 */

import * as Sentry from "@sentry/react";

const BASE_URL = "/api/v1";

class ApiError extends Error {
  constructor(status, message, detail = null) {
    super(message);
    this.status = status;
    this.detail = detail;
    this.name = "ApiError";
  }
}

let isRefreshing = false;

async function refreshAuth() {
  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });
  return res.ok;
}

async function request(endpoint, options = {}) {
  const { body, ...opts } = options;

  const config = {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...opts.headers,
    },
    ...opts,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  let response = await fetch(`${BASE_URL}${endpoint}`, config);

  // 401 retry: attempt refresh once, then retry original request
  if (response.status === 401 && !isRefreshing && !endpoint.startsWith("/auth/")) {
    isRefreshing = true;
    try {
      const refreshed = await refreshAuth();
      if (refreshed) {
        response = await fetch(`${BASE_URL}${endpoint}`, config);
      }
    } finally {
      isRefreshing = false;
    }
  }

  if (!response.ok) {
    let detail = null;
    try {
      detail = await response.json();
    } catch {
      // pas de JSON dans la réponse
    }

    // If still 401 after refresh attempt, redirect to login
    if (response.status === 401) {
      // Avoid redirect loop: don't redirect if already on /login
      if (window.location.pathname !== "/login") {
        const redirect = window.location.pathname + window.location.search;
        window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`;
      }
      throw new ApiError(401, "Session expirée");
    }

    const error = new ApiError(
      response.status,
      detail?.detail || `Erreur ${response.status}`,
      detail
    );
    if (response.status >= 500) {
      Sentry.captureException(error, {
        contexts: { api: { endpoint, method: config.method || "GET", status: response.status } },
      });
    }
    throw error;
  }

  // 204 No Content
  if (response.status === 204) return null;

  return response.json();
}

// --- Méthodes HTTP ---

export const api = {
  get: (endpoint, params) => {
    const query = params ? "?" + new URLSearchParams(params).toString() : "";
    return request(`${endpoint}${query}`);
  },

  post: (endpoint, data) =>
    request(endpoint, { method: "POST", body: data }),

  put: (endpoint, data) =>
    request(endpoint, { method: "PUT", body: data }),

  patch: (endpoint, data) =>
    request(endpoint, { method: "PATCH", body: data }),

  delete: (endpoint) =>
    request(endpoint, { method: "DELETE" }),

  /**
   * Upload un fichier via POST multipart/form-data.
   * Le navigateur gère le boundary automatiquement (pas de Content-Type manuel).
   */
  upload: async (endpoint, formData) => {
    let response = await fetch(`${BASE_URL}${endpoint}`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    // 401 retry
    if (response.status === 401 && !isRefreshing) {
      isRefreshing = true;
      try {
        const refreshed = await refreshAuth();
        if (refreshed) {
          response = await fetch(`${BASE_URL}${endpoint}`, {
            method: "POST",
            credentials: "include",
            body: formData,
          });
        }
      } finally {
        isRefreshing = false;
      }
    }

    if (!response.ok) {
      let detail = null;
      try {
        detail = await response.json();
      } catch {
        // pas de JSON
      }
      if (response.status === 401) {
        if (window.location.pathname !== "/login") {
          const redirect = window.location.pathname + window.location.search;
          window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`;
        }
        throw new ApiError(401, "Session expirée");
      }
      const error = new ApiError(
        response.status,
        detail?.detail || `Erreur ${response.status}`,
        detail
      );
      if (response.status >= 500) {
        Sentry.captureException(error, {
          contexts: { api: { endpoint, method: "POST (upload)", status: response.status } },
        });
      }
      throw error;
    }

    if (response.status === 204) return null;
    return response.json();
  },

  /**
   * Télécharger un fichier via POST (rapport .docx).
   * Retourne un Blob + déclenche le téléchargement navigateur.
   */
  download: async (endpoint, data) => {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let detail = null;
      try {
        detail = await response.json();
      } catch {
        // pas de JSON
      }
      const error = new ApiError(
        response.status,
        detail?.detail || `Erreur téléchargement`,
        detail
      );
      if (response.status >= 500) {
        Sentry.captureException(error, {
          contexts: { api: { endpoint, method: "POST (download)", status: response.status } },
        });
      }
      throw error;
    }
    // Extraire le nom de fichier du header Content-Disposition
    const disposition = response.headers.get("Content-Disposition");
    let filename = "rapport.docx";
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }
    const blob = await response.blob();
    // Déclencher le téléchargement dans le navigateur
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return blob;
  },

  /**
   * Télécharger un fichier via GET.
   * Déclenche le téléchargement dans le navigateur.
   */
  downloadGet: async (endpoint) => {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      let detail = null;
      try {
        detail = await response.json();
      } catch {
        // pas de JSON
      }
      const error = new ApiError(
        response.status,
        detail?.detail || `Erreur téléchargement`,
        detail
      );
      if (response.status >= 500) {
        Sentry.captureException(error, {
          contexts: { api: { endpoint, method: "GET (download)", status: response.status } },
        });
      }
      throw error;
    }
    const disposition = response.headers.get("Content-Disposition");
    let filename = "rapport.docx";
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return blob;
  },
};

export { ApiError };
export default api;
